import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import { z } from 'zod/v4';

import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  useAxiosInstance,
} from './utils';

const URL = '/group/invoice_totals';

type LoadGroupInvoiceTotalsVariables = { companies: ReadonlyArray<string> };

const AmountSchema = z.preprocess(val => Number(val), z.number());

const CompanyInvoiceTotalSchema = z.object({
  company: z.string(),
  name: z.string(),
  totalInvoices: AmountSchema,
  clients: z.number().optional(),
});

export const GroupInvoiceTotalsSchema = z.object({
  disputed: AmountSchema,
  paused: AmountSchema,
  actionable: AmountSchema,
  totalInvoices: AmountSchema,
  notDue: AmountSchema,
  thirdParty: AmountSchema,
  companies: z.array(CompanyInvoiceTotalSchema).optional(),
});

export type GroupInvoiceTotals = z.infer<typeof GroupInvoiceTotalsSchema>;

async function loadGroupInvoiceTotalsQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: LoadGroupInvoiceTotalsVariables,
) {
  const instance = await axiosInstance;
  const { data } = await instance.get(URL, { params: variables });

  const parseResult = GroupInvoiceTotalsSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'groupInvoiceTotals');
    throw new SilentError();
  }

  return parseResult.data;
}

export function useLoadGroupInvoiceTotals(variables: LoadGroupInvoiceTotalsVariables) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<GroupInvoiceTotals, ApiError>({
    queryKey: ['group-invoice-totals', variables],
    queryFn: () => loadGroupInvoiceTotalsQueryFn(axiosInstance, variables),
  });

  return addResourceNameToQueryResult<GroupInvoiceTotals, ApiError, 'groupInvoiceTotals'>(
    'groupInvoiceTotals',
    queryResult,
  );
}
